import { Box, Link, Typography } from "@material-ui/core";
import "./styles.scss";

import {
  isCorpTenant,
  localCache,
  SHOW_POLICIES_IN_PACKAGES,
  useIsTravelApp,
  useShowPolicyBanner,
} from "@capone/common";
import { InfoOutlined } from "@material-ui/icons";
import clsx from "clsx";
import {
  BannerSeverity,
  CorpCompareBarBanner,
  CorpCompareBarTooltip,
  CorpPolicyBanner,
  GenericShopListFooter,
  HotelAvailabilityCard,
  Icon,
  IconName,
  NotificationBanner,
  OutOfPolicyModal,
  useDeviceTypes,
  isSpendAndGetOffer,
  SanitizedHtml,
} from "halifax";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import InView from "react-intersection-observer";
import ReactList from "react-list";
import { RouteComponentProps } from "react-router";
import {
  CorpLodging,
  CorpLodgingData,
  FlightRatingsEnum,
  getLodgingTrackingProperties,
  IIdLodgings,
  Lodging,
  PackagedLodging,
  PackageFetchDetailsEnum,
  SELECTED_PACKAGE_HOTEL_FROM_LIST,
  SELECTED_UNAVAILABLE_PACKAGE_HOTEL_FROM_LIST,
  SliceStopCountFilter,
  TripDetails,
  VIEWED_PACKAGE_LODGING_IN_LIST,
  ViewedCorpRateDescriptorEntryPoints,
} from "redmond";
import { v4 as uuidv4 } from "uuid";
import { config } from "../../../../api/config";
import { trackEvent } from "../../../../api/v0/trackEvent";
import { ClientContext } from "../../../../App";
import {
  AVAILABLE,
  CACHE_HOTEL_TOKEN,
  CORP_DEBUGGING_PANEL,
  getExperimentVariant,
  PACKAGES_LIST_OPTIMIZATIONS_V1,
  useExperiments,
  XMOD_V1_EXPERIMENT,
} from "../../../../context/experiments";
import { onOpenCompareBarTooltip } from "../../../../utils/events";
import {
  getLowestAvailableFareByRating,
  getSelectedFareClass,
} from "../../../../utils/fareClass";
import { PATH_HOME, PATH_HOTEL_SHOP } from "../../../../utils/paths";
import { transformToStringifiedQuery } from "../../../hotel-shop/utils/queryStringHelpers";
import {
  initialFareclassOptionFilter,
  initialFilterOptions,
} from "../../../search/reducer";
import { setOpenDatesModal } from "../../actions/actions";
import { PackagesAvailabilityCallState } from "../../reducer/state";
import { AvailabilityFilter } from "../AvailabilityFilter";
import { AvailabilityMap } from "../AvailabilityMap";
import { AvailabilityNoResults } from "../AvailabilityNoResults";
import { AvailabilitySearchControl } from "../AvailabilitySearchControl";
import { AvailabilitySort } from "../AvailabilitySort";
import { CorporateDebuggingPanel } from "../CorporateDebuggingPanel";
import { RecommendedFlightsCard } from "../RecommendedFlightsCard";
import { AvailabilityListConnectorProps } from "./container";
import * as textConstants from "./textConstants";

const APPROXIMATE_HOTEL_AVAILABILITY_CARD_HEIGHT = 284;

export interface IAvailabilityListProps
  extends AvailabilityListConnectorProps,
    RouteComponentProps {
  showFullMap?: boolean;
  setShowFullMap?: (showFullMap: boolean) => void;
}

export const AvailabilityList = (props: IAvailabilityListProps) => {
  const {
    nightCount,
    accountReferenceId,
    lodgings,
    packagesAvailabilityCallState,
    fetchMorePackagesAvailability,
    history,
    packagesByLodgingId,
    searchedLocation,
    travelersCount,
    isFilteredHotelAvailabilityLodgingsEmpty,
    recommendedFlights,
    packageHotelQueryParams,
    origin,
    credit,
    packagesViewedHotelListProperties,
    setLodgingIdHovered,
    fareClassFilter,
    setFareClassFilter,
    stopsFilter,
    setStopsFilter,
    showFullMap,
    setShowFullMap,
    hotelIdFilter,
    selectedRewardsAccount,
    canEarnRewards,
  } = props;
  // const [untilProp, setUntilProp] = useState<Date | null>(null);
  // const [fromProp, setFromProp] = useState<Date | null>(null);
  // const [refetchFromCalendarModal, setRefetchFromCalendarModal] =
  //   useState(false);
  const [locationName, setLocationName] = React.useState("");
  const [isSearchTermLodging, setIsSearchTermLodging] = React.useState(false);
  // If search term is location (ex: Toronto) vs if search term is point of interest (ex: Toronto Zoo)
  const [isSearchTermPoint, setIsSearchTermPoint] = React.useState(false);
  const listRef = useRef<ReactList | null>(null);
  const divRef = useRef<HTMLDivElement | null>(null);
  const recommendedFlightsRef = useRef<HTMLDivElement | null>(null);
  const { policies } = useContext(ClientContext);
  const { matchesMobile, matchesDesktop } = useDeviceTypes();
  const [showPolicyModal, setShowPolicyModal] = useState<string | null>(null);
  const [corpPolicyDescriptorViewCount, setCorpPolicyDescriptorViewCount] =
    useState(0);
  const [fareClassCriteriaNotMet, setFareClassCriteriaNotMet] = useState(false);
  const [nonStopFilterCriteriaNotMet, setNonStopFilterCriteriaNotMet] =
    useState(false);

  const showPolicyBanner =
    SHOW_POLICIES_IN_PACKAGES && useShowPolicyBanner(policies);

  const expState = useExperiments();

  const isCorporateDebuggingPanelEnabled =
    getExperimentVariant(expState.experiments, CORP_DEBUGGING_PANEL) ===
    AVAILABLE;

  const cacheHotelTokenEnabled =
    getExperimentVariant(expState.experiments, CACHE_HOTEL_TOKEN) === AVAILABLE;

  const packagesListEnhancementsEnabled =
    getExperimentVariant(
      expState.experiments,
      PACKAGES_LIST_OPTIMIZATIONS_V1
    ) === AVAILABLE;

  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  const { isTravelApp } = useIsTravelApp();

  React.useEffect(() => {
    const [locationName] = searchedLocation?.label
      ? searchedLocation.label.split(",")
      : [];
    setLocationName(locationName);

    const placeTypes = searchedLocation
      ? (searchedLocation.id as IIdLodgings).lodgingSelection.placeTypes
      : [];

    setIsSearchTermLodging(placeTypes.includes("lodging"));
    setIsSearchTermPoint(
      !placeTypes.includes("locality") &&
        !placeTypes.includes("political") &&
        !placeTypes.includes("country")
    );
  }, [searchedLocation]);

  useEffect(() => {
    if (
      packagesAvailabilityCallState ===
        PackagesAvailabilityCallState.FollowUpSearchCallSuccess ||
      packagesAvailabilityCallState ===
        PackagesAvailabilityCallState.InitialSearchCallSuccess
    ) {
      setTimeout(() => fetchMorePackagesAvailability(history), 100);
    }
  }, [packagesAvailabilityCallState, fetchMorePackagesAvailability, history]);

  React.useEffect(() => {
    if (recommendedFlights?.value) {
      const tripDetails: TripDetails = recommendedFlights.value;

      const selectedFareClass = getSelectedFareClass(fareClassFilter);

      const selectedFareRating = selectedFareClass
        ? FlightRatingsEnum[selectedFareClass]
        : FlightRatingsEnum[getLowestAvailableFareByRating(tripDetails)];

      const matchingFare = tripDetails.fareDetails.find((fareDetail) =>
        fareDetail.slices.some(
          (slice) => slice.fareShelf?.rating === selectedFareRating
        )
      );

      setFareClassCriteriaNotMet(false);
      if (!matchingFare) {
        setFareClassCriteriaNotMet(true);

        const lowestFare = getLowestAvailableFareByRating(tripDetails);

        // if selected fare class isn't available, set filter to lowest available fare class
        setFareClassFilter({
          ...initialFareclassOptionFilter,
          [lowestFare]: true,
        });
      }

      setNonStopFilterCriteriaNotMet(false);
      if (stopsFilter === SliceStopCountFilter.NONE) {
        const hasNonStop = tripDetails.slices.some(
          (slice) => slice.stops === 0
        );

        if (!hasNonStop) {
          setNonStopFilterCriteriaNotMet(true);
          setStopsFilter(initialFilterOptions.stopsOption);
        }
      }
    } else {
      setFareClassCriteriaNotMet(false);
      setNonStopFilterCriteriaNotMet(false);
    }
  }, [recommendedFlights?.value]);

  const sortedLodgings = useMemo(() => {
    // SORT
    const searchedLodgingSelection = (searchedLocation?.id as IIdLodgings)
      ?.lodgingSelection;

    if (
      searchedLodgingSelection &&
      searchedLodgingSelection.placeTypes.includes("lodging")
    ) {
      const searchTermName = searchedLodgingSelection.searchTerm.split(",")[0];
      return lodgings
        .slice()
        .sort(
          (a, b) =>
            b.lodging.name.toLowerCase().indexOf(searchTermName.toLowerCase()) -
            a.lodging.name.toLowerCase().indexOf(searchTermName.toLowerCase())
        );
    }
    return lodgings;
  }, [lodgings, searchedLocation]);

  const renderEachLodging = (
    lodgingData: Lodging | CorpLodging,
    index: number,
    packagedLodging?: PackagedLodging
  ) => {
    const { isFreeCancel, lodging, bestPromotionThisLodging, isPreferred } =
      lodgingData;
    const available =
      typeof lodgingData.available === "undefined"
        ? true
        : lodgingData.available;

    const hasPolicyCompliance = "corporateTravel" in lodging;
    const isInPolicy =
      hasPolicyCompliance &&
      (lodging.corporateTravel.policyCompliance.isInPolicy ?? true);

    // const policyReasons = hasPolicyCompliance
    //   ? lodging.corporateTravel.policyCompliance.reasons
    //   : [];

    const onOpenPolicyDescriptor = () => {
      if (corpPolicyDescriptorViewCount <= 5) {
        // trackEvent({
        //   eventName: VIEWED_POLICY_DESCRIPTOR,
        //   properties: {
        //     type: POLICY_DESCRIPTOR,
        //     entry_point: PolicyDescriptorEntryPoints.HOTELS_LIST,
        //     funnel: "hotels",
        //     policy_reason: policyReasons.join(", "),
        //   },
        // });
        setCorpPolicyDescriptorViewCount((prevState) => prevState + 1);
      }
    };
    const showCorpCompareBarBanner =
      matchesDesktop && Boolean(lodgingData.hasCorporateRates);

    return (
      <Link
        id={`availability-row-${index}`}
        className={clsx("availability-row", {
          unavailable: !available,
          "nav-improvement": true,
        })}
        component="button"
        key={lodging.id}
        onClick={() => {
          if (available) {
            const packageLodging = packagesByLodgingId?.[lodging.id];
            const packagePricingDetails =
              packageLodging?.packageDetails?.pricing;

            const packageDiscount =
              packagePricingDetails?.totalPackageSavings.fiat.value ?? 0;

            const packageDiscountPercentage =
              packageDiscount && packagePricingDetails
                ? (packageDiscount /
                    packagePricingDetails?.strikethroughTotalPrice.fiat.value) *
                  100
                : 0;

            trackEvent({
              eventName: SELECTED_PACKAGE_HOTEL_FROM_LIST,
              properties: {
                ...packagesViewedHotelListProperties.properties,
                ...getLodgingTrackingProperties(lodgingData).properties,
                is_preferred_cot: isPreferred,
                lodging_row_index: index,
                package_discount: packageDiscount,
                package_discount_percentage: packageDiscountPercentage,
                is_corporate: lodgingData?.hasCorporateRates,
                top_booked: lodgingData.highlights?.isTopSeller,
                searched_hotel: hotelIdFilter === lodging.id,
                meal_plan:
                  packageLodging?.packageDetails?.lodgingMealPlan?.kind,
              },
              encryptedProperties: [
                ...packagesViewedHotelListProperties.encryptedProperties,
                ...getLodgingTrackingProperties(lodgingData)
                  .encryptedProperties,
              ],
            });
            const params = transformToStringifiedQuery({
              lodgingId: lodging.id,
              origin: origin?.id?.code?.code,
              destination: (searchedLocation?.id as IIdLodgings)
                .lodgingSelection.searchTerm,
              placeId: (searchedLocation?.id as IIdLodgings).lodgingSelection
                .placeId,
              selectedLodgingIndex: index,
              ...packageHotelQueryParams,
              shopToken: cacheHotelTokenEnabled
                ? packagedLodging?.opaqueShopRequest
                : localCache.set(
                    uuidv4(),
                    packagedLodging?.opaqueShopRequest || ""
                  ).key,
              isTravelApp,
              selectedLodgingIsTopSeller: lodgingData.highlights?.isTopSeller,
              selectedLodgingMealPlan:
                packageLodging?.packageDetails.lodgingMealPlan?.kind,
              selectedLodgingIsSearched: hotelIdFilter === lodging.id,
              lodgingSelectedFromMap: false,
            });
            if (matchesMobile)
              history.push(`${PATH_HOTEL_SHOP}${params}`, {
                fromPage: location.pathname,
              });
            else window.open(`${PATH_HOTEL_SHOP}${params}`, "_blank");
          } else {
            trackEvent({
              eventName: SELECTED_UNAVAILABLE_PACKAGE_HOTEL_FROM_LIST,
              properties: {
                ...packagesViewedHotelListProperties.properties,
                lodging_row_index: index,
              },
              encryptedProperties:
                packagesViewedHotelListProperties.encryptedProperties,
            });
          }
        }}
        onMouseEnter={() => {
          setLodgingIdHovered(lodging.id);
        }}
        onMouseLeave={() => setLodgingIdHovered(null)}
      >
        <InView
          as="div"
          onChange={(inView) => {
            if (inView) {
              const searchedLodgingSelection = (
                searchedLocation?.id as IIdLodgings
              )?.lodgingSelection;

              const nightlyTotalWithTaxesAndFees =
                lodgingData.price?.breakdown &&
                "taxesAndFees" in lodgingData.price?.breakdown
                  ? (lodgingData.price?.nightlyPrice.fiat.value || 0) +
                    (lodgingData.price?.breakdown.taxesAndFees?.fiat.value ||
                      0) /
                      (nightCount || 1)
                  : lodgingData.price?.nightlyPrice.fiat.value || 0;

              const isSearchedLodging =
                searchedLodgingSelection &&
                searchedLodgingSelection.placeTypes.includes("lodging") &&
                lodgingData.lodging.name.startsWith(
                  searchedLodgingSelection.searchTerm.split(",")[0]
                );

              trackEvent({
                eventName: VIEWED_PACKAGE_LODGING_IN_LIST,
                properties: {
                  ...packagesViewedHotelListProperties.properties,
                  ...getLodgingTrackingProperties(lodgingData).properties,
                  lodging_row_index: index,
                  lodging_id: lodging.id,
                  lodging_name: lodging.name,
                  promotion_applicable:
                    lodgingData?.bestPromotionThisLodging?.promotionType,
                  is_preferred: lodgingData?.isPreferred,
                  is_available: lodgingData?.available,
                  google_place_id: searchedLodgingSelection?.placeId,
                  daily_owed_including_taxes_fees_usd:
                    nightlyTotalWithTaxesAndFees,
                  daily_owed_excluding_taxes_fees_usd:
                    lodgingData.price?.nightlyPrice.fiat.value,
                  is_compare_to_bar: showCorpCompareBarBanner,
                  is_searched: isSearchedLodging,
                  is_corporate: lodgingData?.hasCorporateRates,
                  is_top_booked: lodgingData.highlights?.isTopSeller,
                },
                encryptedProperties: [
                  ...packagesViewedHotelListProperties.encryptedProperties,
                  ...getLodgingTrackingProperties(lodgingData)
                    .encryptedProperties,
                ],
              });
            }
          }}
          threshold={0.8}
        >
          <Box
            className={clsx("hotel-availability-card-wrapper", {
              unavailable: !available,
              // preferred: shouldMerchandisePreferred(lodgingData),
              // "on-sale": showOnSaleBanner,
              "capone-corporate": isCorpTenant(config.TENANT),
              "show-policy-banner": showPolicyBanner,
              "in-policy": showPolicyBanner && isInPolicy,
            })}
          >
            {hotelIdFilter === lodging.id ? (
              <div
                className={clsx("hotel-name-filter-banner", {
                  mobile: matchesMobile,
                })}
              >
                <Icon name={IconName.MagnifyingGlass} />
                <Typography
                  variant="body1"
                  className="hotel-name-filter-banner-text"
                >
                  {textConstants.HOTEL_NAME_FILTER_BANNER_TEXT}
                </Typography>
              </div>
            ) : undefined}
            <CorpPolicyBanner
              variant={matchesDesktop ? "descriptor" : "base"}
              productType="hotel"
              corporateTravel={
                showPolicyBanner
                  ? (lodging as CorpLodgingData)?.corporateTravel
                  : undefined
              }
              limit={policies?.hotels.policies[0].maxPricePerNight}
              onOpen={() => onOpenPolicyDescriptor()}
              {...(matchesMobile && { borderRadius: 0 })}
            />
            <CorpCompareBarBanner
              show={showCorpCompareBarBanner}
              roundedTopCorners={!showPolicyBanner}
              onOpen={onOpenCompareBarTooltip(
                ViewedCorpRateDescriptorEntryPoints.HOTELS_LIST_CARD
              )}
            />
            <Box
              className={clsx("hotel-availability-card-content", {
                "with-hotel-name-filter-banner": hotelIdFilter === lodging.id,
              })}
            >
              <HotelAvailabilityCard
                hotelAvailabilityInfo={{
                  bestPromotionThisLodging,
                  lodging,
                  price: available
                    ? packagesByLodgingId?.[lodging.id]?.packageDetails.pricing
                    : undefined,
                  nightCount,
                  // TODO: ask about where to get city name only (e.g.: Toronto)
                  neighbourhood: searchedLocation?.label,
                  rewardsKey: accountReferenceId || undefined,
                  available: lodgingData.available,
                  bestOfferThisLodging:
                    lodgingData.bestOfferThisLodging &&
                    !isSpendAndGetOffer(
                      lodgingData.bestOfferThisLodging.descriptions
                    )
                      ? lodgingData.bestOfferThisLodging
                      : undefined,
                  searchedRoomCount: 1,
                  highlights: lodgingData.highlights,
                }}
                earnTagContent={
                  selectedRewardsAccount && canEarnRewards ? (
                    <>
                      <Icon name={IconName.StarIcon} />
                      <SanitizedHtml
                        as={Typography}
                        className="earn-tag-text"
                        html={textConstants.getEarnTagText(
                          selectedRewardsAccount.earn.hotelsMultiplier,
                          selectedRewardsAccount.rewardsBalance
                            .currencyDescription ??
                            selectedRewardsAccount.rewardsBalance.currency
                        )}
                      />
                    </>
                  ) : undefined
                }
                earnTagClassName="b2b"
                isGlobalMobileNavExperiment
                variant="package"
                travelersCount={travelersCount}
                isFreeCancel={isFreeCancel}
                isFreeBreakfastCancelExperiment
                isShowMealPlanExperiment
                isMobile={matchesMobile}
                showOffer={
                  lodgingData.bestOfferThisLodging?.amount.amount !==
                    credit?.amount.amount &&
                  !(
                    lodgingData.bestOfferThisLodging &&
                    isSpendAndGetOffer(
                      lodgingData.bestOfferThisLodging.descriptions
                    )
                  )
                }
                tenant={config.TENANT}
                isLoyaltyEligible={Boolean(lodgingData?.loyaltyProgramCode)}
                hasCorporateRates={Boolean(lodgingData.hasCorporateRates)}
                mealPlanKind={
                  packagesListEnhancementsEnabled
                    ? packagesByLodgingId?.[lodging.id]?.packageDetails
                        .lodgingMealPlan?.kind
                    : undefined
                }
                showTopSellerHighlight={packagesListEnhancementsEnabled}
                useCondensedHighlightTags={packagesListEnhancementsEnabled}
                showEnhancedPackagePricing={packagesListEnhancementsEnabled}
              />
            </Box>
          </Box>
        </InView>
      </Link>
    );
  };

  const containerRef = useRef<null | HTMLElement>(null);

  useEffect(() => {
    if (recommendedFlightsRef.current && matchesMobile) {
      setTimeout(() => {
        recommendedFlightsRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 1000);
    }
  }, [recommendedFlightsRef]);

  return (
    <Box
      className={clsx("availability-list-root", {
        mobile: matchesMobile,
        "list-enhancements": packagesListEnhancementsEnabled,
        map: showFullMap,
        xmod: isXmodRedesign,
      })}
      {...{ ref: containerRef }}
    >
      {isCorporateDebuggingPanelEnabled && (
        <CorporateDebuggingPanel lodgings={lodgings} />
      )}
      {showPolicyModal && SHOW_POLICIES_IN_PACKAGES && (
        <OutOfPolicyModal
          isOpen={!!showPolicyModal}
          isMobile={matchesMobile}
          onClose={() => {
            setShowPolicyModal(null);
            // trackEvent(
            //   getClickCancelOOPModalEvent(
            //     ModalScreens.HOTELS_AVAILABILITY,
            //     "hotels",
            //     modalType
            //   )
            // );
          }}
          onContinue={() => {
            window.open(showPolicyModal, "_blank");
            setShowPolicyModal(null);
            // trackEvent(
            //   getClickContinueOOPModalEvent(
            //     ModalScreens.HOTELS_AVAILABILITY,
            //     "hotels",
            //     modalType
            //   )
            // );
          }}
          isApprovalRequired={
            policies?.settings && policies.settings.isApprovalRequired
          }
        />
      )}
      <Box className="availability-list-container">
        {!matchesMobile && (
          <AvailabilitySearchControl
            packagesListEnhancementsEnabled={packagesListEnhancementsEnabled}
            isOnMapScreen={showFullMap}
          />
        )}

        {showFullMap ? (
          <AvailabilityMap onlyShowFilteredLodgings />
        ) : (
          <div className="availability-list-content">
            {!matchesMobile && packagesListEnhancementsEnabled ? (
              <div className="availability-list-content-sidebar">
                <AvailabilityMap
                  isPreview
                  onClick={() => setShowFullMap?.(true)}
                />
                <AvailabilityFilter
                  variant="content"
                  immediateApply
                  showMealPlanTypeFilter
                  showDiscountFilter
                  useHotelNameAutocomplete
                  trackingLocation="list"
                />
              </div>
            ) : undefined}

            <div className="availability-list-content-main">
              {isCorpTenant(config.TENANT) && (
                <Box
                  className={clsx("availability-list-corp-bar-banner", {
                    mobile: matchesMobile,
                  })}
                >
                  <Icon name={IconName.TagIcon} />
                  <CorpCompareBarTooltip
                    label={
                      <Typography>
                        {textConstants.CORP_COMPARE_TO_BAR_BANNER_TEXT}
                        {matchesMobile && <InfoOutlined fontSize="small" />}
                      </Typography>
                    }
                    additionalTooltipContent={
                      <Typography
                        style={{ fontStyle: "italic", marginTop: "10px" }}
                      >
                        Look for the price tag icon to find hotels with these
                        rates!
                      </Typography>
                    }
                    onOpen={onOpenCompareBarTooltip(
                      ViewedCorpRateDescriptorEntryPoints.HOTELS_LIST_HEADER
                    )}
                    hideIcon={matchesMobile}
                  />
                </Box>
              )}
              {recommendedFlights?.type === PackageFetchDetailsEnum.Flight && (
                <div
                  ref={recommendedFlightsRef}
                  className="recommended-flghts-section"
                >
                  <Typography className="recommended-flights-header">
                    {isCorpTenant(config.TENANT)
                      ? textConstants.CORPORATE_RECOMMENDED_FLIGHTS_HEADER
                      : textConstants.RECOMMENDED_FLIGHTS_HEADER}
                  </Typography>
                  {fareClassCriteriaNotMet && (
                    <NotificationBanner
                      content={
                        <Typography className="search-criteria-warning-banner-text">
                          {
                            textConstants.SEARCHED_FARE_CLASS_NOT_FOUND_BANNER_TEXT
                          }
                        </Typography>
                      }
                      severity={BannerSeverity.WARNING}
                      className="pkg-search-criteria-warning-banner"
                    />
                  )}
                  {nonStopFilterCriteriaNotMet && (
                    <NotificationBanner
                      content={
                        <Typography className="search-criteria-warning-banner-text">
                          {textConstants.NO_NONSTOP_FLIGHTS_FOUND}
                        </Typography>
                      }
                      severity={BannerSeverity.WARNING}
                      className="pkg-search-criteria-warning-banner"
                    />
                  )}
                  <RecommendedFlightsCard isMobile={matchesMobile} />
                </div>
              )}
              {!matchesMobile && (
                <Box className="availability-list-heading-sort">
                  {locationName ? (
                    <Typography
                      variant="h4"
                      className="availability-list-count-heading"
                    >
                      {textConstants.HOTEL_COUNT_HEADING_TEXT(
                        lodgings.filter(
                          (lodging) =>
                            lodging.available || !("available" in lodging)
                        ).length,
                        locationName,
                        isSearchTermLodging,
                        isSearchTermPoint
                      )}
                    </Typography>
                  ) : (
                    <div />
                  )}
                  <AvailabilitySort />
                </Box>
              )}

              {isFilteredHotelAvailabilityLodgingsEmpty ? (
                <Box className="no-hotels-found-components-section">
                  <AvailabilityNoResults />
                </Box>
              ) : (
                <div ref={divRef} className="availability-list">
                  <ReactList
                    ref={listRef}
                    itemRenderer={(index: number) => {
                      if (
                        packagesAvailabilityCallState ===
                        PackagesAvailabilityCallState.InitialMapSearchCallInProcess
                      ) {
                        return (
                          <HotelAvailabilitySkeleton
                            key={index}
                            isMobile={matchesMobile}
                          />
                        );
                      }
                      if (index < sortedLodgings.length) {
                        // [Packages TO-DO]: Update to render correct components based on callstate
                        return (
                          <React.Fragment key={index}>
                            {renderEachLodging(
                              lodgings[index],
                              index,
                              packagesByLodgingId?.[lodgings[index].lodging.id]
                            )}
                          </React.Fragment>
                        );
                      }
                      // render CardSkeleton when it's not Complete
                      if (
                        packagesAvailabilityCallState !==
                        PackagesAvailabilityCallState.Complete
                      ) {
                        return (
                          <HotelAvailabilitySkeleton
                            key={index}
                            isMobile={matchesMobile}
                          />
                        );
                      }
                      // render MoreResults footer when it's Complete
                      if (
                        packagesAvailabilityCallState ===
                        PackagesAvailabilityCallState.Complete
                      ) {
                        return (
                          <>
                            <GenericShopListFooter
                              className={clsx(
                                "availability-list-find-more-results",
                                "b2b"
                              )}
                              title={textConstants.FIND_MORE_RESULTS_TITLE_TEXT}
                              subtitle={
                                textConstants.FIND_MORE_RESULTS_SUBTITLE_TEXT
                              }
                              buttons={[
                                {
                                  title: textConstants.ADJUST_DATES_TEXT,
                                  className: clsx(
                                    "find-more-results-button",
                                    "adjust-dates",
                                    "b2b"
                                  ),
                                  isPrimary: true,
                                  onClick: () => setOpenDatesModal(true),
                                },
                                {
                                  title: textConstants.SEARCH_AGAIN_TEXT,
                                  className: clsx(
                                    "find-more-results-button",
                                    "search-again",
                                    "b2b"
                                  ),
                                  isPrimary: false,
                                  onClick: () => history.push(PATH_HOME),
                                },
                              ]}
                              isMobile={matchesMobile}
                            />
                          </>
                        );
                      }

                      return <></>;
                    }}
                    length={lodgings.length + 1}
                    type="variable"
                    itemSizeEstimator={() =>
                      APPROXIMATE_HOTEL_AVAILABILITY_CARD_HEIGHT
                    }
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </Box>
    </Box>
  );
};

interface IHotelAvailabilitySkeletonProps {
  isMobile: boolean;
}

const HotelAvailabilitySkeleton = (props: IHotelAvailabilitySkeletonProps) => {
  const { isMobile } = props;

  return (
    <Box className="availability-skeleton-row">
      <Box className="hotel-availability-card-wrapper">
        <HotelAvailabilityCard isSkeleton isMobile={isMobile} />
      </Box>
    </Box>
  );
};
